/**
 * Detects the delimiter used in folder paths
 * - Gmail uses "/" and has "[Gmail]/" prefix for special folders
 * - Most other servers use "." as delimiter
 * @param {Array} folders - Array of folder objects with path property
 * @returns {string} The detected delimiter ("." or "/")
 */
export function detectDelimiter(folders) {
  for (const folder of folders) {
    if (folder.path.includes('[Gmail]/')) return '/';
    // Check for common patterns - if path has "/" but no ".", use "/"
    if (folder.path.includes('/') && !folder.path.includes('.')) return '/';
  }
  return '.';
}

/**
 * Parses folder path into segments, handling special cases
 * - "[Gmail]/All Mail" -> ["[Gmail]", "All Mail"]
 * - "INBOX.Test.Subfolder" -> ["INBOX", "Test", "Subfolder"]
 * @param {string} path - The folder path
 * @param {string} delimiter - The delimiter to use
 * @returns {Array<string>} Array of path segments
 */
export function parsePathSegments(path, delimiter) {
  if (delimiter === '/') {
    // Handle [Gmail] prefix specially - it's a virtual parent
    if (path.startsWith('[Gmail]/')) {
      const rest = path.slice('[Gmail]/'.length);
      return ['[Gmail]', ...rest.split('/').filter(Boolean)];
    }
    return path.split('/').filter(Boolean);
  }
  return path.split(delimiter).filter(Boolean);
}

/**
 * Converts flat folder array to hierarchical tree structure
 * Creates virtual parent nodes for paths that don't have their own folder
 *
 * @param {Array} folders - Flat array of folder objects
 * @returns {{ tree: Array, virtualFolders: Array<string> }} Tree structure and list of virtual folder paths
 *
 * FolderNode = {
 *   ...originalFolder,
 *   children: FolderNode[],
 *   isVirtual: boolean,  // true for virtual parent that doesn't exist as actual folder
 *   depth: number
 * }
 */
export function foldersToTree(folders) {
  if (!folders || folders.length === 0) {
    return { tree: [], virtualFolders: [] };
  }

  const delimiter = detectDelimiter(folders);
  const tree = [];
  const pathMap = new Map(); // path -> node
  const virtualFolders = new Set();

  // Sort folders by path to ensure parents are processed first
  const sortedFolders = [...folders].sort((a, b) =>
    a.path.localeCompare(b.path)
  );

  for (const folder of sortedFolders) {
    const segments = parsePathSegments(folder.path, delimiter);
    let currentLevel = tree;
    let currentPath = '';

    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      const isLast = i === segments.length - 1;

      // Build the current path
      if (delimiter === '/' && segment === '[Gmail]') {
        currentPath = '[Gmail]';
      } else {
        currentPath = currentPath ? `${currentPath}${delimiter}${segment}` : segment;
      }

      let existingNode = pathMap.get(currentPath);

      if (!existingNode) {
        const node = isLast
          ? {
              ...folder,
              children: [],
              isVirtual: false,
              depth: i,
            }
          : {
              id: `virtual-${currentPath}`,
              name: segment,
              path: currentPath,
              children: [],
              isVirtual: true,
              depth: i,
              unseenCount: 0,
              noSelect: true,
            };

        if (!isLast) {
          virtualFolders.add(currentPath);
        }

        currentLevel.push(node);
        pathMap.set(currentPath, node);
        existingNode = node;
      } else if (isLast && existingNode.isVirtual) {
        // Replace virtual node with real folder data
        Object.assign(existingNode, folder, {
          children: existingNode.children,
          isVirtual: false,
          depth: i,
        });
        virtualFolders.delete(currentPath);
      }

      currentLevel = existingNode.children;
    }
  }

  return { tree, virtualFolders: Array.from(virtualFolders) };
}

/**
 * Sorts tree applying folderOrder for known system folders
 * Recursively sorts all levels of the tree
 *
 * @param {Array} tree - Tree structure from foldersToTree
 * @param {Array<string>} folderOrder - Array of folder names in preferred order
 * @returns {Array} Sorted tree
 */
export function sortFolderTree(tree, folderOrder) {
  const sorted = [...tree].sort((a, b) => {
    const indexA = folderOrder.indexOf(a.name);
    const indexB = folderOrder.indexOf(b.name);

    // Both in folderOrder -> sort by their order
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    // Only A is in folderOrder -> A comes first
    if (indexA !== -1) return -1;
    // Only B is in folderOrder -> B comes first
    if (indexB !== -1) return 1;
    // Neither in folderOrder -> sort alphabetically
    return a.name.localeCompare(b.name);
  });

  // Recursively sort children
  for (const node of sorted) {
    if (node.children && node.children.length > 0) {
      node.children = sortFolderTree(node.children, folderOrder);
    }
  }

  return sorted;
}

/**
 * Flattens tree to get all folder IDs for SortableContext
 * Returns IDs in depth-first order
 *
 * @param {Array} tree - Tree structure
 * @returns {Array<string|number>} Array of folder IDs
 */
export function flattenFolderIds(tree) {
  const ids = [];

  function traverse(nodes) {
    for (const node of nodes) {
      ids.push(node.id);
      if (node.children && node.children.length > 0) {
        traverse(node.children);
      }
    }
  }

  traverse(tree);
  return ids;
}

/**
 * Finds a folder in the tree by ID
 *
 * @param {Array} tree - Tree structure
 * @param {string|number} id - Folder ID to find
 * @returns {Object|null} The folder node or null if not found
 */
export function findFolderInTree(tree, id) {
  for (const node of tree) {
    if (node.id === id) return node;
    if (node.children && node.children.length > 0) {
      const found = findFolderInTree(node.children, id);
      if (found) return found;
    }
  }
  return null;
}
